import type { Criterion, CriterionProgress, VolumeUnit } from "./types";

/**
 * Trading volume is always shown in millions on the canvas ("5.87/50 million
 * USD", "0 million"), so the raw USD figure is scaled and trimmed to at most
 * two decimals with no trailing zeros.
 */
export function formatMillions(valueUSD: number): string {
  const millions = valueUSD / 1_000_000;
  return millions
    .toLocaleString("en-US", { maximumFractionDigits: 2 })
    .replace(/,/g, "");
}

/** Client-count criteria render as plain whole numbers ("10/10", "8/10"). */
export function formatCount(value: number): string {
  return Math.round(value).toLocaleString("en-US");
}

/** `tv` is a USD volume shown in millions; `ac`/`nc` are plain counts. */
export function formatCriterionValue(
  criterion: Criterion,
  value: number,
): string {
  return criterion === "tv" ? formatMillions(value) : formatCount(value);
}

/** Fraction of `target` reached, clamped to 0–1. `0` target means no bar fill. */
export function progressRatio(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(1, Math.max(0, current / target));
}

/**
 * Trading volume still needed this month: the target minus every earlier
 * month, floored at 0. `null` when there is no `tv` target to measure against.
 */
export function currentMonthRemaining(
  progress: CriterionProgress,
): number | null {
  const { criterion, target, months } = progress;
  if (criterion !== "tv" || target <= 0 || months.length === 0) return null;
  const previous = months.slice(0, -1).reduce((sum, row) => sum + row.value, 0);
  return Math.max(0, target - previous);
}

/**
 * A USD trading volume restated in the unit the partner picked in the Volume
 * Converter — millions of USD by default, otherwise lots of one symbol.
 */
export function convertVolume(usdValue: number, unit: VolumeUnit): number {
  if (unit.kind === "usd") return usdValue / 1_000_000;
  return unit.perLotUsd > 0 ? usdValue / unit.perLotUsd : 0;
}

/** Stable identity for a unit, so two selections can be compared. */
export function volumeUnitKey(unit: VolumeUnit): string {
  return unit.kind === "usd" ? "usd" : unit.symbol;
}

/** Lot counts carry thousands separators and at most two decimals. */
export function formatLots(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/**
 * Lot notionals in the converter's reference list keep up to five decimals —
 * two would collapse the FX rows ("US$1.17979" → "US$1.18") into a value too
 * coarse to convert a volume with.
 */
export function formatLotNotional(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 5 });
}

/**
 * `convertVolume` + the matching numeric formatting for the value row. The USD
 * branch defers to `formatMillions` so the default display is byte-identical to
 * what the widget rendered before the converter existed.
 */
export function formatVolumeInUnit(usdValue: number, unit: VolumeUnit): string {
  return unit.kind === "usd"
    ? formatMillions(usdValue)
    : formatLots(convertVolume(usdValue, unit));
}

/** Rebate rates render with at most two decimals ("4.92", "0.01", "0"). */
export function formatRebateRate(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
